import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

const LoadingSpinner = memo(({ 
  size = 'md', 
  color = 'primary', 
  text = null,
  className = '' 
}) => {
  // Size configurations
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8', 
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  // Color configurations
  const colors = {
    primary: 'border-[#9ACD32]',
    white: 'border-white',
    gray: 'border-gray-400',
    dark: 'border-gray-800',
  };

  const spinnerSize = sizes[size] || sizes.md;
  const spinnerColor = colors[color] || colors.primary;

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <motion.div
        className={`${spinnerSize} border-4 border-t-transparent rounded-full ${spinnerColor}`}
        animate={{ rotate: 360 }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }}
      />
      
      {text && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-3 text-sm text-gray-600 font-medium"
        >
          {text}
        </motion.p>
      )}
    </div>
  );
});

LoadingSpinner.displayName = 'LoadingSpinner';

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  color: PropTypes.oneOf(['primary', 'white', 'gray', 'dark']),
  text: PropTypes.string,
  className: PropTypes.string,
};

// Alternative pulse loading animation
export const LoadingPulse = memo(({ className = '' }) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="w-2 h-2 bg-[#9ACD32] rounded-full"
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          delay: i * 0.2
        }}
      />
    ))}
  </div>
));

LoadingPulse.displayName = 'LoadingPulse';

// Skeleton loading component
export const LoadingSkeleton = memo(({ 
  width = 'w-full', 
  height = 'h-4', 
  className = '' 
}) => (
  <motion.div
    className={`${width} ${height} bg-gray-200 rounded ${className}`}
    animate={{
      opacity: [0.5, 1, 0.5]
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }}
  />
));

LoadingSkeleton.displayName = 'LoadingSkeleton';

LoadingSkeleton.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  className: PropTypes.string,
};

export default LoadingSpinner;